/** Today (V3Today): Dukun's brief, totals, what needs you, what's next, bot activity. */
import { Crew } from "../components/Crew.tsx";
import { Shell } from "../components/Shell.tsx";
import { Empty, Loading, Page, Stat } from "../components/ui.tsx";
import { useApi } from "../lib/data.ts";
import { ago, nextGrid, pct, signedUsd, timeUtc, until, usd } from "../lib/format.ts";
import type { Overview, OverviewBot } from "../lib/overview.ts";
import { Link } from "../lib/router.tsx";
import type { User } from "../lib/types.ts";
import { NeedCard, type Need } from "./Needs.tsx";

function greeting(now = new Date()) { const h = now.getHours(); return h < 5 ? "Good night." : h < 12 ? "Good morning." : h < 18 ? "Good afternoon." : "Good evening."; }

function brief(bots: OverviewBot[]): string {
  if (!bots.length) return "You have no bots yet. Start one on paper from a template: it trades with pretend money on real prices, so you can watch it before risking anything.";
  const total = bots.reduce((s, b) => s + (b.last_equity ?? b.starting_balance), 0);
  const day = bots.reduce((s, b) => s + (b.last_equity !== null && b.equity_24h !== null ? b.last_equity - b.equity_24h : 0), 0);
  const trouble = bots.filter((b) => b.status === "halted" || b.status === "error");
  const parts = [`${trouble.length ? `${bots.length - trouble.length} of ${bots.length} bots are` : bots.length === 1 ? "Your bot is" : `All ${bots.length} bots are`} inside their limits and ${bots.length > 1 ? "together are" : "is"} worth ${usd(total)}, ${day >= 0 ? "up" : "down"} ${usd(Math.abs(day))} today.`];
  for (const b of bots) {
    if (b.status === "halted") parts.push(`${b.name} hit its safety stop and closed everything; it waits for you.`);
    else if (b.status === "error") parts.push(`${b.name} stopped on an error and needs a look.`);
    else if (b.status === "paused") parts.push(`${b.name} is paused and keeps its positions.`);
    else if (b.drawdown !== null && b.drawdown > 0.005) parts.push(`${b.name} is ${(b.drawdown * 100).toFixed(1)}% below its best${b.throttle !== null && b.throttle < 1 ? ` and trading at ${Math.round(b.throttle * 100)}% size to be careful` : " and still trading at full size"}.`);
    else if (!b.last_equity_at) parts.push(`${b.name} starts at the next hourly close.`);
  }
  return parts.join(" ");
}

export function Today({ user }: { user: User }) {
  const { data, loading } = useApi<Overview>("/overview", { refreshMs: 60_000 });
  const { data: needs } = useApi<{ needs: Need[] }>("/needs", { refreshMs: 60_000 });
  const bots = data?.bots ?? [];
  const total = bots.reduce((s, b) => s + (b.last_equity ?? b.starting_balance), 0), start = bots.reduce((s, b) => s + b.starting_balance, 0);
  const day = bots.reduce((s, b) => s + (b.last_equity !== null && b.equity_24h !== null ? b.last_equity - b.equity_24h : 0), 0);
  const grid = nextGrid();
  const now = new Date();
  return (
    <Shell crumb="Today">
      <Page eyebrow={now.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })} title={greeting(now)}>
        {loading ? <Loading /> : (
          <section aria-label="Brief" className="card flex gap-4 p-5 md:p-6">
            <Crew character="nebula" size={44} />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2"><span className="font-semibold">Dukun</span><span className="text-[13px] text-muted">Brief · {now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span></div>
              <p className="m-0 mt-2 text-[16px] leading-[1.65]">{brief(bots)}</p>
              {bots.length > 0 && (
                <div className="mt-4 grid grid-cols-[repeat(auto-fit,minmax(min(150px,100%),1fr))] gap-2.5">
                  <Stat label={bots.length > 1 ? "All bots" : "Value"} value={usd(total)} />
                  <Stat label="Today" value={signedUsd(day)} tone={day >= 0 ? "up" : "down"} />
                  <Stat label="Since start" value={pct(start ? (total / start - 1) * 100 : 0)} tone={total >= start ? "up" : "down"} />
                </div>
              )}
            </div>
          </section>
        )}

        {!!needs?.needs.length && (
          <section aria-labelledby="needs-h" className="flex flex-col gap-3.5">
            <div className="flex items-baseline justify-between"><h2 id="needs-h" className="h2">Needs you <span className="font-normal text-muted">{needs.needs.length}</span></h2><Link to="/needs" className="text-[13px]">See all</Link></div>
            {needs.needs.slice(0, 2).map((n) => <NeedCard key={n.id} need={n} />)}
          </section>
        )}

        {!loading && !bots.length && <Empty title="No bots yet"><Link to="/bots/new" className="btn-primary mt-3 no-underline hover:no-underline">Start a paper bot</Link></Empty>}

        {bots.length > 0 && (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(300px,100%),1fr))] gap-6">
            <section aria-labelledby="next-h" className="flex flex-col gap-3">
              <h2 id="next-h" className="h2">Next up</h2>
              <div className="card overflow-hidden">
                {[
                  { title: "Rebalance", who: bots.filter((b) => b.status === "active").map((b) => b.name).join(", ") || "No active bots", when: `${timeUtc(grid).slice(-9)} · ${until(grid)}` },
                  { title: "Funding settles", who: bots.filter((b) => b.market !== "spot").map((b) => b.name).join(", ") || "—", when: timeUtc(grid).slice(-9) },
                  { title: "Hourly check", who: "All bots", when: until(Math.ceil(Date.now() / 3_600_000) * 3_600_000 + 5_000) },
                ].map((x) => (
                  <div key={x.title} className="flex items-center gap-3 border-b border-soft px-4 py-3.5 last:border-0">
                    <span className="h-2.5 w-2.5 rounded-full border-2 border-line-strong" aria-hidden="true" />
                    <span className="min-w-0 flex-1"><span className="block text-[15px]">{x.title}</span><span className="block truncate text-[13px] text-muted">{x.who}</span></span>
                    <span className="whitespace-nowrap text-[13px] text-muted">{x.when}</span>
                  </div>
                ))}
              </div>
            </section>
            <section aria-labelledby="act-h" className="flex flex-col gap-3">
              <h2 id="act-h" className="h2">Bot activity</h2>
              <div className="flex flex-col gap-1 pt-1">
                {data!.activity.length === 0 && <div className="text-muted">Nothing yet. Bots act on the hour; rebalances happen at 00:00, 08:00 and 16:00 UTC.</div>}
                {data!.activity.map((a, i) => (
                  <Link key={i} to={`/bots/${a.account_id}`} className="flex items-start gap-3 rounded-[10px] p-1.5 text-ink no-underline hover:bg-soft hover:no-underline">
                    <Crew character={a.character} size={28} />
                    <span className="min-w-0 flex-1"><span className="block text-[14px] leading-normal"><strong className="font-semibold">{a.name}</strong> {a.message.charAt(0).toLowerCase() + a.message.slice(1)}</span><span className="block text-[12px] text-muted">{ago(a.ts)}</span></span>
                  </Link>
                ))}
              </div>
            </section>
          </div>
        )}
        <p className="m-0 text-[12px] text-muted">Signed in as {user.email}. Paper trading: real prices, pretend money.</p>
      </Page>
    </Shell>
  );
}
